import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { USER_ROLE_MODEL } from '@model';
import { getValueFromSearchParam } from '@utils/history';
import useProjectId from '@hooks/useProjectId';
import { useHistory } from 'react-router-dom';
import { useProjectPermission } from 'src/container/useProjectPermission';
import { ErrorBackTo } from '@components/error';

// Toggle for debugging
const DEBUG_PERMISSIONS = true; // Set to false in production

const userRoleMap = {
  [USER_ROLE_MODEL.ADMIN]: 'isAdmin',
  [USER_ROLE_MODEL.INTERNAL]: 'isInternal',
  [USER_ROLE_MODEL.CLIENT]: 'isClient',
};

/**
 * Get user info from localStorage (gateway-userInfo key)
 */
const getLocalUserInfo = () => {
  let userInfo = null;
  try {
    const storedUserInfo = window.localStorage.getItem('gateway-userInfo');

    if (storedUserInfo) {
      userInfo = JSON.parse(storedUserInfo);

      // if (DEBUG_PERMISSIONS) {
      //   console.log('=== GATEWAY USER INFO ===');
      //   console.log('User:', userInfo?.name);
      //   console.log('Email:', userInfo?.email);
      //   console.log('Role:', userInfo?.role);
      //   console.log('isAdmin:', userInfo?.isAdmin);
      //   console.log('isInternal:', userInfo?.isInternal);
      //   console.log('isClient:', userInfo?.isClient);
      //   console.log('Permissions:', userInfo?.permissions);
      //   console.log('=========================');
      // }
    }
  } catch (e) {
    console.error('Error parsing gateway-userInfo:', e);
  }
  return userInfo;
};

const defaultRedirectPath = (projectId) =>
  `/error?backTo=${ErrorBackTo.projectDetail}&projectId=${projectId}`;

function PermissionCheck({ children, permission, loadingComponent = null }) {
  const { getRedirectPath = defaultRedirectPath } = permission || {};

  // ✅ Get user info from gateway-userInfo localStorage
  const userInfoFromLocalStorage = getLocalUserInfo();
  const userInfoFromRedux = useSelector(({ login }) => login.userInfo);

  // Priority: localStorage first, then Redux
  const userInfo = userInfoFromLocalStorage || userInfoFromRedux;

  const projectId = useProjectId();
  const history = useHistory();

  const {
    projectCompanyInfo,
    originAccessibleModules,
    originIntegrationData,
    permissionLoading,
    sessionTimeout,
    isLogin,
  } = useSelector(({ common, projects, login }) => ({
    projectCompanyInfo: common?.projectCompanyInfo,
    originAccessibleModules: projects?.originAccessibleModules,
    originIntegrationData: projects?.projectDetail?.originIntegrationData,
    permissionLoading: projects?.permissionLoading,
    sessionTimeout: login.sessionTimeout,
    isLogin: login.isLogin,
  }));

  useProjectPermission();

  // Debug logging
  // if (DEBUG_PERMISSIONS) {
  //   console.log('=== PERMISSION CHECK ===');
  //   console.log('Path:', window.location.pathname);
  //   console.log('User:', userInfo?.name, `(${userInfo?.role})`);
  //   console.log('Permission Config:', permission);
  //   console.log('Project Role:', projectCompanyInfo?.currentUserPermission);
  //   console.log('Module List:', originAccessibleModules?.moduleList);
  // }

  const visible = useMemo(() => {
    if (permissionLoading) {
      if (DEBUG_PERMISSIONS) console.log('⏳ Permission loading...');
      return false;
    }

    if (!permission) {
      if (DEBUG_PERMISSIONS) console.log('✅ No permission required');
      return true;
    }

    let permissionValid = true;

    // 1. User Role Check
    if (permission?.userRole?.length) {
      const userRoleCheck = permission.userRole.some((role) => {
        const roleKey = userRoleMap[role]; // e.g., 'admin' -> 'isAdmin'
        return userInfo?.[roleKey] === true;
      });

      if (DEBUG_PERMISSIONS) {
        console.log('User Role Check:', {
          required: permission.userRole,
          userIsAdmin: userInfo?.isAdmin,
          userIsInternal: userInfo?.isInternal,
          userIsClient: userInfo?.isClient,
          result: userRoleCheck ? '✅ PASS' : '❌ FAIL',
        });
      }

      permissionValid = permissionValid && userRoleCheck;
    }

    // 2. Project Role Check
    if (permission?.projectRole?.length) {
      const projectRoleCheck = permission.projectRole.some(
        (role) => role === projectCompanyInfo?.currentUserPermission,
      );

      // if (DEBUG_PERMISSIONS) {
      //   console.log('Project Role Check:', {
      //     required: permission.projectRole,
      //     current: projectCompanyInfo?.currentUserPermission,
      //     result: projectRoleCheck ? '✅ PASS' : '❌ FAIL',
      //   });
      // }

      permissionValid = permissionValid && projectRoleCheck;
    }

    // 3. Module Check
    if (permission?.checkCustomizedModule) {
      const { identifier, getIdentifier } = permission;
      const fromTiles = getValueFromSearchParam('from')?.from === 'tiles';
      const permissionIdentifier = identifier || getIdentifier?.();

      if (!permissionIdentifier) {
        if (DEBUG_PERMISSIONS) console.log('❌ No module identifier');
        return false;
      }

      if (fromTiles) {
        if (
          !originAccessibleModules?.homepageSectionList?.includes('navigator')
        ) {
          if (DEBUG_PERMISSIONS) console.log('❌ No navigator access');
          return false;
        }
        const availableTabs =
          originIntegrationData?.integrationModuleWithTabList?.filter(
            (i) =>
              i.permission === 'Both' ||
              (i.permission === 'Internal Only' && userInfo?.isInternal),
          );
        const tileCheck = availableTabs?.some?.((tab) =>
          tab.moduleList?.find?.((i) => permissionIdentifier === i.moduleId),
        );
        if (DEBUG_PERMISSIONS) {
          console.log('Tiles Check:', tileCheck ? '✅ PASS' : '❌ FAIL');
        }
        return tileCheck;
      }

      const moduleCheck =
        originAccessibleModules?.moduleList?.includes?.(permissionIdentifier);
      if (DEBUG_PERMISSIONS) {
        console.log('Module Check:', {
          identifier: permissionIdentifier,
          moduleList: originAccessibleModules?.moduleList,
          result: moduleCheck ? '✅ PASS' : '❌ FAIL',
        });
      }
      permissionValid = permissionValid && moduleCheck;
    }

    if (DEBUG_PERMISSIONS) {
      console.log(
        'Final Result:',
        permissionValid ? '✅ ALLOWED' : '❌ DENIED',
      );
      console.log('========================');
    }

    return permissionValid;
  }, [
    permission,
    permissionLoading,
    originAccessibleModules,
    projectCompanyInfo?.currentUserPermission,
    userInfo,
    originIntegrationData,
  ]);

  if (permissionLoading) {
    return loadingComponent;
  }

  if (sessionTimeout || !isLogin || !userInfo) {
    if (DEBUG_PERMISSIONS) {
      console.log('❌ Access Denied - Session/Login Issue');
      // console.log({ sessionTimeout, isLogin, hasUserInfo: !!userInfo });
    }
    return null;
  }

  if (!visible) {
    if (DEBUG_PERMISSIONS) {
      console.log('❌ Redirecting - No Permission');
      // console.log('Redirect to:', getRedirectPath(projectId));
    }
    history.replace(getRedirectPath(projectId));
    return null;
  }

  return children;
}

export default PermissionCheck;
